// HQ → Instagram: the main account, one account per branch, and the public comment reply for each.
// The token is stored encrypted and never shown again. The webhook is one fixed callback, so it is not shown here.
// Each account shows its connection: the @username its token belongs to, until when the token is valid (the server
// refreshes it), and the last check. Check now runs that check at once.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api, { formatDate, formatDateTime } from '../services/api';
import BranchSelect from '../components/BranchSelect';
import { Loader, PageHead, Spinner } from '../components/ui';

const SOURCE = {
  saved: 'Using the token saved here',
  environment: 'Using the token set on the server',
  none: 'No token yet',
};

const NUMBER_NOTE = '{whatsapp_number} is the branch WhatsApp number, or the main WhatsApp number when the branch has none.';

const EMPTY_BRANCH = {
  franchise_id: '',
  label: '',
  account_id: '',
  access_token: '',
  comment_replies_enabled: true,
  comment_reply_text: '',
};

function ReplyFields({ enabled, text, onEnabled, onText }) {
  return (
    <>
      <label className="de-check">
        <input type="checkbox" checked={enabled} onChange={(e) => onEnabled(e.target.checked)} />
        Reply to comments
      </label>
      <label className="de-field">
        <span className="de-label">Comment reply</span>
        <textarea
          className="de-input de-input--bordered"
          rows={3}
          value={text}
          onChange={(e) => onText(e.target.value)}
          placeholder="Leave blank for: Hi @{username}! Please DM us, or continue with us on WhatsApp at {whatsapp_number}."
        />
        <span className="de-small de-muted">{NUMBER_NOTE} Use {'{username}'} for the commenter's name.</span>
      </label>
    </>
  );
}

function Health({ account, busy, onCheck }) {
  if (!account) return null;
  const tone = account.healthy === true ? 'ok' : account.healthy === false ? 'danger' : 'muted';
  const label = account.healthy === true ? 'Connected' : account.healthy === false ? 'Needs attention' : 'Not checked yet';
  return (
    <div className="de-small" style={{ marginTop: 6 }}>
      <span className={`de-pill de-pill--sm de-pill--${tone}`}>{label}</span>
      {account.username ? <span style={{ marginLeft: 8 }}>@{account.username}</span> : null}
      {account.token_expires_at ? <span className="de-muted"> · token valid until {formatDate(account.token_expires_at)}</span> : null}
      {account.last_check_at ? <span className="de-muted"> · checked {formatDateTime(account.last_check_at)}</span> : null}
      {onCheck ? (
        <button type="button" className="de-btn de-btn--outline de-btn--sm" style={{ marginLeft: 8 }} disabled={busy} onClick={onCheck}>
          {busy ? <Spinner /> : null}
          Check now
        </button>
      ) : null}
      {account.healthy === false && account.last_check_result ? (
        <div className="de-danger-text" style={{ marginTop: 4 }}>{account.last_check_result}</div>
      ) : null}
    </div>
  );
}

export default function InstagramSettings() {
  const [data, setData] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [token, setToken] = useState('');
  const [repliesOn, setRepliesOn] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY_BRANCH);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState('');

  const applyMain = (d) => {
    setData(d);
    setAccountId(d.account_id || '');
    setToken('');
    setRepliesOn(d.comment_replies_enabled !== false);
    setReplyText(d.comment_reply_text || '');
  };

  const loadAccounts = () =>
    api.listInstagramAccounts().then((d) => setAccounts(Array.isArray(d.accounts) ? d.accounts : [])).catch((e) => toast.error(e.message));

  const load = () => {
    api.getInstagramConfig().then(applyMain).catch((e) => toast.error(e.message));
    loadAccounts();
    api.branches().then((rows) => setBranchOptions(Array.isArray(rows) ? rows : [])).catch(() => setBranchOptions([]));
  };

  useEffect(() => {
    load();
  }, []);

  const saveMain = async () => {
    setSaving(true);
    try {
      const d = await api.saveInstagramConfig({
        account_id: accountId.trim(),
        access_token: token.trim(),
        comment_replies_enabled: repliesOn,
        comment_reply_text: replyText.trim(),
      });
      applyMain(d);
      if (d.subscribe_error) toast.error(d.subscribe_error);
      else toast.success(d.configured ? 'Instagram saved' : 'Saved');
      loadAccounts();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const disconnectMain = async () => {
    if (!window.confirm('Disconnect the main Instagram account? Its account id and token are cleared and its messages and comments are no longer answered.')) return;
    setSaving(true);
    try {
      applyMain(await api.disconnectInstagram());
      toast.success('Main Instagram account disconnected');
      loadAccounts();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveBranch = async () => {
    setBusy('add');
    try {
      const d = await api.addInstagramAccount({
        ...draft,
        franchise_id: Number(draft.franchise_id),
        account_id: draft.account_id.trim(),
        access_token: draft.access_token.trim(),
        comment_reply_text: draft.comment_reply_text.trim(),
      });
      if (d.subscribe_error) toast.error(d.subscribe_error);
      else toast.success('Branch Instagram saved');
      setDraft(EMPTY_BRANCH);
      setAdding(false);
      loadAccounts();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  const saveEdit = async () => {
    setBusy('edit');
    try {
      const d = await api.updateInstagramAccount(editing.id, {
        account_id: editing.account_id.trim(),
        access_token: (editing.access_token || '').trim(),
        label: (editing.label || '').trim(),
        comment_replies_enabled: editing.comment_replies_enabled,
        comment_reply_text: (editing.comment_reply_text || '').trim(),
      });
      if (d.subscribe_error) toast.error(d.subscribe_error);
      else toast.success('Instagram account saved');
      setEditing(null);
      loadAccounts();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  const checkNow = async (id, isMain) => {
    setBusy(`check${id}`);
    try {
      const d = await api.checkInstagramAccount(id);
      if (d.ok) toast.success('Instagram account works');
      else toast.error(d.message);
      if (isMain) api.getInstagramConfig().then(applyMain).catch(() => {});
      loadAccounts();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  const turnOn = async (row) => {
    setBusy(`on${row.id}`);
    try {
      const d = await api.enableInstagramAccount(row.id);
      if (d.subscribe_error) toast.error(d.subscribe_error);
      else toast.success('Branch Instagram turned on');
      loadAccounts();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  const turnOff = async (row) => {
    setBusy(`off${row.id}`);
    try {
      await api.disableInstagramAccount(row.id);
      toast.success('Branch Instagram turned off');
      loadAccounts();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  if (!data) return <Loader />;
  const branchAccounts = accounts.filter((row) => !row.is_main);
  const setDraftField = (key) => (value) => setDraft((current) => ({ ...current, [key]: value }));

  return (
    <div className="de-fade">
      <PageHead
        title="Instagram"
        sub="Connect the main Instagram account, and a separate account for a branch. Comment replies are worded here. Direct messages still open in Conversations."
      />
      <div className="de-callout" style={{ marginBottom: 16 }}>
        <strong>{data.configured ? 'Main account connected' : 'Main account not connected'}</strong>
        <div className="de-small" style={{ marginTop: 4 }}>{SOURCE[data.source] || SOURCE.none}</div>
        {data.configured ? <Health account={data} busy={busy === `check${data.id}`} onCheck={() => checkNow(data.id, true)} /> : null}
        {data.configured ? (
          <button type="button" className="de-btn de-btn--outline de-btn--sm de-mt-8" disabled={saving} onClick={disconnectMain}>
            Disconnect
          </button>
        ) : null}
      </div>
      <div className="de-form-grid" style={{ maxWidth: 640 }}>
        <label className="de-field">
          <span className="de-label">Instagram account id *</span>
          <input className="de-input de-input--bordered de-mono" value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="From Meta, the Instagram professional account id" />
        </label>
        <label className="de-field">
          <span className="de-label">Access token{data.token_saved ? '' : ' *'}</span>
          <input
            className="de-input de-input--bordered de-mono"
            type="password"
            autoComplete="new-password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={data.token_saved ? 'Saved. Type a new token only to replace it.' : 'Instagram access token'}
          />
        </label>
        <ReplyFields enabled={repliesOn} text={replyText} onEnabled={setRepliesOn} onText={setReplyText} />
        <div>
          <button type="button" className="de-btn de-btn--primary" disabled={saving || !accountId.trim()} onClick={saveMain}>
            {saving ? <Spinner light /> : null}
            Save main account
          </button>
        </div>
      </div>

      <div className="de-section-label" style={{ marginTop: 28 }}>Branch accounts</div>
      <p className="de-small de-muted" style={{ marginTop: 0 }}>
        A branch account answers only its own messages. Its comment reply uses that branch's WhatsApp number when the branch has one.
      </p>
      {branchAccounts.length === 0 ? <p className="de-muted">No branch Instagram account yet.</p> : null}
      {branchAccounts.map((row) => (
        <div key={row.id} className="de-callout" style={{ marginBottom: 10, maxWidth: 640 }}>
          {editing && editing.id === row.id ? (
            <div className="de-form-grid">
              <label className="de-field">
                <span className="de-label">Name</span>
                <input className="de-input de-input--bordered" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
              </label>
              <label className="de-field">
                <span className="de-label">Instagram account id *</span>
                <input className="de-input de-input--bordered de-mono" value={editing.account_id} onChange={(e) => setEditing({ ...editing, account_id: e.target.value })} />
              </label>
              <label className="de-field">
                <span className="de-label">Access token</span>
                <input className="de-input de-input--bordered de-mono" type="password" autoComplete="new-password" value={editing.access_token || ''} onChange={(e) => setEditing({ ...editing, access_token: e.target.value })} placeholder="Saved. Type a new token only to replace it." />
              </label>
              <ReplyFields
                enabled={editing.comment_replies_enabled}
                text={editing.comment_reply_text}
                onEnabled={(value) => setEditing({ ...editing, comment_replies_enabled: value })}
                onText={(value) => setEditing({ ...editing, comment_reply_text: value })}
              />
              <div className="de-row" style={{ gap: 8 }}>
                <button type="button" className="de-btn de-btn--primary" disabled={busy === 'edit' || !editing.account_id.trim()} onClick={saveEdit}>
                  {busy === 'edit' ? <Spinner light /> : null}
                  Save
                </button>
                <button type="button" className="de-btn de-btn--outline" onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <strong>{row.label || row.branch_name}</strong>
              {row.status === 'disabled' ? <span className="de-pill de-pill--sm de-pill--muted" style={{ marginLeft: 8 }}>turned off</span> : null}
              <div className="de-small de-muted" style={{ marginTop: 4 }}>
                {row.branch_name} · {row.account_id || 'No account id'} · {row.token_saved ? 'Token saved' : 'No token'} · {row.comment_replies_enabled ? 'Comment replies on' : 'Comment replies off'}
              </div>
              {row.status !== 'disabled' ? <Health account={row} busy={busy === `check${row.id}`} onCheck={() => checkNow(row.id, false)} /> : null}
              {row.comment_reply_text ? <div className="de-small" style={{ marginTop: 6 }}>{row.comment_reply_text}</div> : null}
              {row.status !== 'disabled' ? (
                <div className="de-row" style={{ gap: 8, marginTop: 8 }}>
                  <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setEditing({ ...row, access_token: '' })}>Edit</button>
                  <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === `off${row.id}`} onClick={() => turnOff(row)}>Turn off</button>
                </div>
              ) : (
                <div className="de-row" style={{ gap: 8, marginTop: 8 }}>
                  <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === `on${row.id}`} onClick={() => turnOn(row)}>
                    {busy === `on${row.id}` ? <Spinner /> : null}
                    Turn on again
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      ))}

      {adding ? (
        <div className="de-form-grid" style={{ maxWidth: 640, marginTop: 12 }}>
          <div className="de-field">
            <span className="de-label">Branch *</span>
            <BranchSelect
              branches={branchOptions}
              value={draft.franchise_id}
              onChange={setDraftField('franchise_id')}
              placeholder="Select a branch"
            />
          </div>
          <label className="de-field">
            <span className="de-label">Name</span>
            <input className="de-input de-input--bordered" value={draft.label} onChange={(e) => setDraftField('label')(e.target.value)} placeholder="Optional. Defaults to the branch name." />
          </label>
          <label className="de-field">
            <span className="de-label">Instagram account id *</span>
            <input className="de-input de-input--bordered de-mono" value={draft.account_id} onChange={(e) => setDraftField('account_id')(e.target.value)} />
          </label>
          <label className="de-field">
            <span className="de-label">Access token *</span>
            <input className="de-input de-input--bordered de-mono" type="password" autoComplete="new-password" value={draft.access_token} onChange={(e) => setDraftField('access_token')(e.target.value)} />
          </label>
          <ReplyFields
            enabled={draft.comment_replies_enabled}
            text={draft.comment_reply_text}
            onEnabled={setDraftField('comment_replies_enabled')}
            onText={setDraftField('comment_reply_text')}
          />
          <div className="de-row" style={{ gap: 8 }}>
            <button type="button" className="de-btn de-btn--primary" disabled={busy === 'add' || !draft.franchise_id || !draft.account_id.trim() || !draft.access_token.trim()} onClick={saveBranch}>
              {busy === 'add' ? <Spinner light /> : null}
              Save branch account
            </button>
            <button type="button" className="de-btn de-btn--outline" onClick={() => { setAdding(false); setDraft(EMPTY_BRANCH); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" className="de-btn de-btn--primary" style={{ marginTop: 8 }} onClick={() => setAdding(true)}>Add branch account</button>
      )}
    </div>
  );
}
